import { ButtonHTMLAttributes, forwardRef } from 'react';
import { twMerge as cn } from 'tailwind-merge';

export type ButtonVariant =
  | 'default'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'link';

export type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const BASE_STYLES =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0";

const VARIANT_STYLES: Record<ButtonVariant, string> = {
  default: 'bg-brand text-white hover:bg-brand-hover',
  outline:
    'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-brand',
  secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200',
  ghost: 'hover:bg-gray-100 hover:text-brand',
  destructive: 'bg-red-600 text-white hover:bg-red-700',
  link: 'text-accent underline-offset-4 hover:underline',
};

const SIZE_STYLES: Record<ButtonSize, string> = {
  default: 'h-9 px-4 py-2',
  sm: 'h-8 rounded-md px-3',
  lg: 'h-10 rounded-md px-6',
  icon: 'h-9 w-9',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = 'default', size = 'default', type, ...props },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={cn(
          BASE_STYLES,
          VARIANT_STYLES[variant],
          SIZE_STYLES[size],
          className
        )}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
